/**
 * Common invoice calculation utility
 * This ensures consistent calculation across Add, List, and View pages
 */

import { PAYMENT_STATUS } from '../../../constants/paymentStatus';

/**
 * Calculate invoice totals from line items
 * Based on the logic from AddInvoicePage which correctly handles VAT-included products
 * 
 * @param {Array} lineItems - Array of invoice line items
 * @param {Number} vatPercentage - VAT percentage (e.g., 21 for 21%)
 * @param {Number} discountValue - Discount value (default 0)
 * @param {String} discountType - 'percentage' or 'fixed' (default 'percentage')
 * @returns {Object} Calculated totals
 */
export const calculateInvoiceTotals = (lineItems = [], vatPercentage = 21, discountValue = 0, discountType = 'percentage') => {
  // Calculate total excluding VAT from line items
  const totalExcl = lineItems.reduce((sum, item) => sum + Number(item.TotalExcl || 0), 0);
  
  // Calculate gross total (including VAT)
  // If TotalIncl is available in line items, use it (this is the correct gross amount)
  // Otherwise, calculate from TotalExcl + VAT
  let totalGross = 0;
  const hasStoredTotalIncl = lineItems.length > 0 && lineItems[0].TotalIncl !== undefined;
  
  if (hasStoredTotalIncl) {
    // Sum stored TotalIncl values (these already have VAT included correctly)
    totalGross = lineItems.reduce((sum, item) => sum + Number(item.TotalIncl || 0), 0);
  } else {
    // Fallback: calculate gross from excl + VAT
    // This assumes VAT was added on top of the net price
    totalGross = totalExcl * (1 + vatPercentage / 100);
  }
  
  // Apply discount on gross amount
  let discountAmount = 0;
  if (discountType === 'percentage') {
    discountAmount = (totalGross * (parseFloat(discountValue) || 0)) / 100;
  } else {
    discountAmount = parseFloat(discountValue) || 0;
  }
  
  // Grand total after discount
  const grandTotal = totalGross - discountAmount;
  
  // Extract VAT from the discounted gross amount
  // This is the correct way to extract VAT when it's included in the gross price
  const vatRate = vatPercentage / 100;
  const totalExclAfterDiscount = grandTotal / (1 + vatRate);
  const totalVAT = grandTotal - totalExclAfterDiscount;
  
  return {
    totalExcl: parseFloat(totalExcl.toFixed(2)),
    totalGross: parseFloat(totalGross.toFixed(2)),
    discountAmount: parseFloat(discountAmount.toFixed(2)),
    totalVAT: parseFloat(totalVAT.toFixed(2)),
    grandTotal: parseFloat(grandTotal.toFixed(2)),
    totalExclAfterDiscount: parseFloat(totalExclAfterDiscount.toFixed(2)),
  };
};

/**
 * Get payment status for an invoice
 * 
 * @param {Number} paidAmount - Amount paid
 * @param {Number} totalAmount - Total invoice amount
 * @returns {Object} Status info with label and color
 */
export const getPaymentStatus = (paidAmount, totalAmount) => {
  const paid = Number(paidAmount) || 0;
  const total = Number(totalAmount) || 0;
  
  if (paid === 0) {
    return { label: PAYMENT_STATUS.NOT_PAID, color: 'error', bgColor: '#d32f2f' };
  } else if (paid >= total) {
    return { label: PAYMENT_STATUS.PAID, color: 'success', bgColor: '#388e3c' };
  } else if (paid > 0 && paid < total) {
    // Partial payment: amount paid is greater than 0 but less than total
    return { label: PAYMENT_STATUS.PARTIAL_PAID, color: 'warning', bgColor: '#fbc02d' };
  } else {
    return { label: PAYMENT_STATUS.NOT_PAID, color: 'error', bgColor: '#d32f2f' }; // Default fallback
  }
};

/**
 * Format date to DD-MM-YYYY
 * 
 * @param {String|Date} date - Date to format
 * @returns {String} Formatted date
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};
